import { useDispatch, useSelector } from 'react-redux';
import { Badge, Button, InlineLink, Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { useScreenResolution } from '@app/hooks';
import {
  refetchAddOnOffers,
  resetAddonConfiguration,
  selectAddOns,
  selectSubscribedEasyCare,
  setSelectedEasyCareProductId,
} from '@app/redux/addOns';

import { PromoDetails } from '../PromoDetails';
import { AddonPromotion } from '../../types';
import { EasyCareProducts } from '../../../../constants';

import styles from '../../styles/grouped-addon.module.scss';

type ListItemProduct = {
  id: string;
  name: string;
  price: string;
  description: string;
  oldPrice: number;
  duration?: number;
  promotions?: AddonPromotion[];
  tag?: string | null;
};

const ListItem = ({
  product: { id, name, price, description, oldPrice, promotions = [], duration = 0, tag = '' },
}: {
  product: ListItemProduct;
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('ShopAddons');
  const promotionAmount = promotions[0]?.amount;

  const { isExtraSmall, isSmall } = useScreenResolution();
  const isMobile = isSmall || isExtraSmall;

  const { selectedEasyCareProductId, configureAddonSuccess } = useSelector(selectAddOns);
  const isSelected = selectedEasyCareProductId === id;

  const handleSelect = () => {
    dispatch(setSelectedEasyCareProductId(id));

    if (!isSelected && configureAddonSuccess) {
      dispatch(resetAddonConfiguration());
      dispatch(refetchAddOnOffers());
    }
  };

  const existingEasyCareAddon = useSelector(selectSubscribedEasyCare);
  const isSubscribed = existingEasyCareAddon?.productTypeId === id;

  const buttonLabel = getButtonLabel(isSubscribed, isSelected, t);
  const terms = getAsurionTerms(t, name as EasyCareProducts);

  return (
    <div className={styles['product-container']}>
      <div className={styles['product-name-and-tag']}>
        <Txt variant="bodySmallBold" mr="4px">
          {name}
        </Txt>
        {tag && <Badge label={tag} />}
      </div>
      <div className={styles['product-content']}>
        <div className={styles['product-details']}>
          <Txt component="span" variant="bodySmallRegular">
            <PromoDetails
              promotionAmount={promotionAmount}
              priceWithoutPromo={oldPrice}
              duration={duration}
              priceAfterDiscountExpiration={price}
            />
          </Txt>
          <Txt variant="smallRegular" color="subtle">
            {description}
          </Txt>
          {terms?.title && (
            <Txt variant="tinyRegular" component="p" mt="8px">
              <Txt variant="tinyRegular" style={{ marginRight: 2 }}>
                {terms?.title}
              </Txt>
              <InlineLink href={terms?.url} target="_blank" rel="noopener noreferrer">
                <Txt variant="tinyRegular">{terms?.linkText}</Txt>
              </InlineLink>
            </Txt>
          )}
        </div>
        <Button
          buttonSize="medium"
          variant={isSubscribed ? 'primary' : 'secondary'}
          fluid={isMobile}
          className={styles['select-button']}
          onClick={handleSelect}
          pressed={isSelected}
          disabled={isSubscribed}
        >
          {buttonLabel}
        </Button>
      </div>
    </div>
  );
};

const getAsurionTerms = (t: (key: string) => string, productName: EasyCareProducts) => {
  const terms = {
    [EasyCareProducts.PLUS]: {
      title: t('groupedEasyCare.asurionTerms.plus.description'),
      linkText: t('groupedEasyCare.asurionTerms.plus.link'),
      url: t('groupedEasyCare.asurionTerms.plus.url'),
    },
    [EasyCareProducts.PREMIUM]: {
      title: t('groupedEasyCare.asurionTerms.premium.description'),
      linkText: t('groupedEasyCare.asurionTerms.premium.link'),
      url: t('groupedEasyCare.asurionTerms.premium.url'),
    },
    [EasyCareProducts.DEFAULT]: undefined,
  };

  return terms[productName];
};

const getButtonLabel = (isSubscribed: boolean, isSelected: boolean, t: (key: string) => string) => {
  if (isSubscribed) {
    return t('groupedEasyCare.button.subscribed');
  }
  if (isSelected) {
    return t('groupedEasyCare.button.selected');
  }
  return t('groupedEasyCare.button.select');
};

export default ListItem;
